import { Button, Form, Modal, Select, TimePicker } from 'antd';
import FormField from 'components/FormField';
import dayjs from 'dayjs';
import { Formik, FormikConfig, FormikProps } from 'formik';
import useSmartField from 'hooks/useSmartField';
import { ReactNode, useCallback, useMemo } from 'react';
import {
  AiModelState,
  defaultValueMap,
  getPrefixName,
  Inline,
  Prefix,
  stores2storeScopes,
} from './share';
import { updateServiceWorkingTime } from 'views/BootGuide/service';
import { useTranslate } from '@tolgee/react';
import { FormOutlined, PlusOutlined } from '@ant-design/icons';
import useModal from 'hooks/useModal';
import produce from 'immer';
import RangeBlur from 'components/RangeBlur';
import styled from '@emotion/styled';
import NormalMode from './NormalMode';

type FV = BootGuide.AiModel.FormValues.ServiceWorkingTime;

function useWeekLabel() {
  const { t } = useTranslate();
  return {
    t,
    weekLabel: useMemo(() => {
      return {
        MONDAY: t('multiTimeSelect.周一'),
        TUESDAY: t('multiTimeSelect.周二'),
        WEDNESDAY: t('multiTimeSelect.周三'),
        THURSDAY: t('multiTimeSelect.周四'),
        FRIDAY: t('multiTimeSelect.周五'),
        SATURDAY: t('multiTimeSelect.周六'),
        SUNDAY: t('multiTimeSelect.周日'),
      };
    }, [t]),
  };
}

export function useGetWorkingTimeText() {
  const { weekLabel, t } = useWeekLabel();

  const getWorkingTimeText = useCallback(
    (value: FV['weekRepeat']) => {
      return value.length
        ? value
            .map(({ dayOfWeeks, timeRanges }) => {
              return t('aide.每周的days timeRepeat', {
                days: dayOfWeeks.map((day) => weekLabel[day]).join(' '),
                timeRepeat: timeRanges.map(({ startAt, endAt }) => `${startAt}-${endAt}`).join(' '),
              });
            })
            .join('，')
        : '';
    },
    [t, weekLabel],
  );

  return {
    weekLabel,
    t,
    getWorkingTimeText,
  };
}

function ServiceWorkingTime({ prefix }: { prefix?: Prefix }) {
  const { t, getWorkingTimeText } = useGetWorkingTimeText();

  const {
    field: { value = [], onChange },
  } = useSmartField<FV['weekRepeat']>(getPrefixName('weekRepeat', prefix));

  const text = getWorkingTimeText(value);

  const { visible, show, hide, meta } = useModal<FV>();

  return (
    <Inline>
      {text}
      <Button
        type="text"
        icon={<FormOutlined />}
        onClick={() =>
          show({
            weekRepeat: value,
            storeScopes: [],
          })
        }
      />
      {meta && (
        <WeekReatForm
          enableReinitialize
          initialValues={meta}
          onSubmit={({ weekRepeat }) => {
            onChange(weekRepeat);
            hide();
          }}
        >
          {(form, { resetForm, handleSubmit }) => {
            return (
              <Modal
                visible={visible}
                width={428}
                title={t('工作时间')}
                okText={t('保存')}
                onOk={() => handleSubmit()}
                onCancel={() => {
                  hide();
                  resetForm();
                }}
              >
                <Form.Item required label={t('时间段')} className="!mb-0" />
                {form}
              </Modal>
            );
          }}
        </WeekReatForm>
      )}
    </Inline>
  );
}

function WeekReatForm({
  children,
  ...otherProps
}: FormikConfig<FV> & {
  children: (form: ReactNode, formProps: FormikProps<FV>) => ReactNode;
}) {
  const { t, weekLabel } = useWeekLabel();

  const weekOptions = useMemo(() => {
    return Object.keys(weekLabel).map((key) => {
      return {
        value: key,
        label: weekLabel[key],
      };
    });
  }, [weekLabel]);

  return (
    <Formik enableReinitialize {...otherProps}>
      {(props) => {
        const {
          values,
          values: { weekRepeat },
          setValues,
        } = props;

        const repeat = weekRepeat.map(({ timeRanges }, index) => {
          return (
            <WeekRange>
              <FormField<FV['weekRepeat'][0]['dayOfWeeks']>
                name={`weekRepeat.${index}.dayOfWeeks`}
                validate={(weeks) => (weeks.length ? undefined : t('multiTimeSelect.请选择星期'))}
              >
                {({ field: { value, onBlur } }) => {
                  return (
                    <Week>
                      <Select
                        mode="multiple"
                        placeholder={t('multiTimeSelect.请选择星期')}
                        options={weekOptions}
                        value={value}
                        onBlur={onBlur}
                        onChange={(value) =>
                          setValues({
                            ...values,
                            weekRepeat: weekRepeat.map((cur, i) => {
                              if (i === index) {
                                return {
                                  ...cur,
                                  dayOfWeeks: value,
                                };
                              }
                              return {
                                ...cur,
                                dayOfWeeks: cur.dayOfWeeks.filter((_) => !value.includes(_)),
                              };
                            }),
                          })
                        }
                      />
                      <span>
                        {index > 0 && (
                          <Button
                            type="link"
                            onClick={() =>
                              setValues({
                                ...values,
                                weekRepeat: [
                                  ...weekRepeat.slice(0, index),
                                  ...weekRepeat.slice(index + 1),
                                ],
                              })
                            }
                          >
                            {t('marketing.删除')}
                          </Button>
                        )}
                      </span>
                    </Week>
                  );
                }}
              </FormField>
              {timeRanges.map((_, index2) => {
                return (
                  <FormField<FV['weekRepeat'][0]['timeRanges'][0]>
                    name={`weekRepeat.${index}.timeRanges.${index2}`}
                    validate={({ startAt, endAt } = { startAt: '', endAt: '' }) => {
                      if (!startAt || !endAt) {
                        return t('multiTimeSelect.请填写时间');
                      }
                      const { endAt: preEnd } = timeRanges[index2 - 1] || {};
                      if (preEnd && dayjs(startAt, 'HH:mm').isBefore(dayjs(preEnd, 'HH:mm'))) {
                        return t('multiTimeSelect.earlierTip');
                      }
                    }}
                  >
                    {({ field: { value: { startAt, endAt } = {}, onChange, onBlur } }) => {
                      return (
                        <Range>
                          <RangeBlur
                            Component={TimePicker.RangePicker}
                            allowClear={false}
                            showSecond={false}
                            format="HH:mm"
                            value={
                              startAt && endAt
                                ? ([dayjs(startAt, 'HH:mm'), dayjs(endAt, 'HH:mm')] as any)
                                : undefined
                            }
                            onChange={(range) => {
                              const [start, end] = range || [];
                              if (start && end) {
                                onChange({
                                  startAt: start.format('HH:mm'),
                                  endAt: end.format('HH:mm'),
                                });
                              }
                            }}
                            onBlur={onBlur}
                          />
                          {index2 === 0 ? (
                            timeRanges.length < 4 && (
                              <Button
                                type="link"
                                onClick={() =>
                                  setValues(
                                    produce(values, (values) => {
                                      values.weekRepeat[index].timeRanges.push({
                                        startAt: '',
                                        endAt: '',
                                      });
                                    }),
                                  )
                                }
                              >
                                {t('添加')}
                              </Button>
                            )
                          ) : (
                            <Button
                              type="link"
                              onClick={() =>
                                setValues(
                                  produce(values, (values) => {
                                    values.weekRepeat[index].timeRanges.splice(index2, 1);
                                  }),
                                )
                              }
                            >
                              {t('marketing.删除')}
                            </Button>
                          )}
                        </Range>
                      );
                    }}
                  </FormField>
                );
              })}
            </WeekRange>
          );
        });

        const form = (
          <div>
            {repeat}
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() =>
                setValues({
                  ...values,
                  weekRepeat: [
                    ...weekRepeat,
                    {
                      dayOfWeeks: [],
                      timeRanges: [
                        {
                          startAt: '',
                          endAt: '',
                        },
                      ],
                    },
                  ],
                })
              }
            >
              {t('knowledge.新增时间段')}
            </Button>
          </div>
        );

        return children(form, props);
      }}
    </Formik>
  );
}

export function ServiceWorkingTimeBootGuide(props: AiModelState<FV>) {
  return (
    <NormalMode
      state={props}
      tips={props.t('您的店铺人工客服的工作时间?')}
      formLabel={props.t('工作时间')}
      getFormContent={(prefix) => <ServiceWorkingTime prefix={prefix} />}
      getFirstInitialValues={useCallback(({ stores, defaultValueRule }) => {
        if (!defaultValueRule) {
          return {
            values: [
              {
                ...defaultValueMap.AI_MODEL_SERVICE_WORKING_TIME,
                storeScopes: stores2storeScopes(stores),
              },
            ],
          };
        }

        const {
          SHOP_CUSTOMER_SERVICE_WORKING_TIME: {
            WORK_TIME: { workTimeRanges },
          },
        } = defaultValueRule;

        return {
          values: [
            {
              storeScopes: stores2storeScopes(stores),
              weekRepeat: workTimeRanges,
            },
          ],
        };
      }, [])}
      updateApi={updateServiceWorkingTime}
      getRemainTips={(remainLength) => props.t('您有x家店铺还未配置', { x: remainLength })}
    />
  );
}

const WeekRange = styled.div`
  padding: 8px;
  background: var(--fett-erp-card-bg);
  margin-bottom: 12px;
`;
const Week = styled.div`
  display: flex;
  align-items: center;
  > span {
    flex: 0 0 80px;
  }
`;
const Range = styled.div`
  display: flex;
  align-items: center;
`;

export default ServiceWorkingTime;
